import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Download,
  Mail,
  TrendingUp,
  Search,
  LogOut,
  RefreshCw,
  FileDown,
  Settings,
  Eye,
  Box,
  Menu,
  X,
  UserPlus,
  Trash2,
  RotateCcw,
  Key,
  Activity,
  Link,
  Save,
  ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { useAdminAuth } from "../context/AdminAuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { admin, token, logout } = useAdminAuth();
  const navigate = useNavigate();
  
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    early_adopter_cap: 100,
    download_link: "",
    stripe_early_adopter_link: "",
    stripe_standard_link: ""
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  
  // Add admin dialog state
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [newAdminName, setNewAdminName] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true
  };

  const fetchData = useCallback(async () => {
    const headers = {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    };
    try {
  const [metricsRes, leadsRes, eventsRes, settingsRes, adminsRes] = await Promise.all([
    axios.get(`${API_URL}/api/admin/metrics`, headers),
    axios.get(`${API_URL}/api/admin/leads`, headers),
    axios.get(`${API_URL}/api/admin/events?limit=50`, headers),
    axios.get(`${API_URL}/api/admin/settings`, headers),
    axios.get(`${API_URL}/api/admin/users`, headers)
  ]);
  
  console.log("LEADS RESPONSE:", leadsRes.data);
  
  setMetrics(metricsRes.data);
  setLeads(leadsRes.data);
  setEvents(eventsRes.data);
  setSettings(settingsRes.data);
  setAdminUsers(adminsRes.data);
      setSettingsForm({
        early_adopter_cap: settingsRes.data.early_adopter_cap || 100,
        download_link: settingsRes.data.download_link || "",
        stripe_early_adopter_link: settingsRes.data.stripe_early_adopter_link || "",
        stripe_standard_link: settingsRes.data.stripe_standard_link || ""
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) {
        await logout();
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [token, fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleSearch = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (tierFilter !== "all") params.append("tier", tierFilter);
      
      const response = await axios.get(
        `${API_URL}/api/admin/leads?${params.toString()}`,
        authHeaders
      );
      setLeads(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleTierChange = async (email, newTier) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/leads/tier`,
        { email, tier: newTier },
        authHeaders
      );
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to update tier");
    }
  };

  const handleResetUsage = async (email) => {
    if (!confirm(`Reset generation count for ${email}?`)) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/leads/reset-usage`,
        { email },
        authHeaders
      );
      await fetchData();
      alert("Usage reset successfully");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to reset usage");
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/admin/leads/export`, "_blank");
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/settings`,
        settingsForm,
        authHeaders
      );
      await fetchData();
      alert("Settings saved!");
    } catch (error) {
      alert("Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail || !newAdminPassword) {
      alert("Email and password are required");
      return;
    }
    
    setAddingAdmin(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users`,
        { 
          email: newAdminEmail, 
          password: newAdminPassword,
          name: newAdminName || null
        },
        authHeaders
      );
      await fetchData();
      setShowAddAdmin(false);
      setNewAdminEmail("");
      setNewAdminPassword("");
      setNewAdminName("");
      alert("Admin user created! They must change password on first login.");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to create admin");
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (userId, userEmail) => {
    if (!confirm(`Delete admin ${userEmail}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, authHeaders);
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete admin");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case "early_adopter":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "standard":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
    }
  };

  const truncateEmail = (email, maxLen = 20) => {
    if (!email) return "-";
    if (email.length <= maxLen) return email;
    return email.substring(0, maxLen) + "...";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="text-white text-sm">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] overflow-x-hidden" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 flex-shrink-0">
              <Box className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              <span className="text-base sm:text-xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                <span className="hidden sm:inline">ParamSnap </span>Admin
              </span>
            </div>
            
            {/* Desktop Actions */}
            <div className="hidden sm:flex items-center gap-3 sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-zinc-400 hover:text-white text-xs sm:text-sm"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${refreshing ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/change-password")}
                className="text-zinc-400 hover:text-white text-xs sm:text-sm"
              >
                <Key className="w-4 h-4 mr-1" />
                <span className="hidden lg:inline">Password</span>
              </Button>
              <span className="text-xs sm:text-sm text-zinc-400 truncate max-w-[120px] sm:max-w-none">{admin?.email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="sm:hidden p-2 -mr-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden py-3 border-t border-white/5">
              <div className="flex flex-col gap-2">
                <span className="text-xs text-zinc-400 truncate">{admin?.email}</span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="text-zinc-400 hover:text-white text-xs h-8 px-2 flex-1"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/admin/change-password")}
                    className="text-zinc-400 hover:text-white text-xs h-8 px-2 flex-1"
                  >
                    <Key className="w-4 h-4 mr-1" />
                    Password
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-zinc-400 hover:text-white h-8 px-2"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 sm:gap-4 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 hide-scrollbar">
          {["overview", "users", "events", "settings", "admins"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab
                  ? "bg-blue-500/20 text-blue-400"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && metrics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Metrics Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <MetricCard title="Visitors" value={metrics.total_visitors} icon={Eye} testId="metric-visitors" />
              <MetricCard title="Signups" value={metrics.total_emails} icon={Mail} testId="metric-emails" />
              <MetricCard title="Downloads" value={metrics.total_downloads} icon={Download} testId="metric-downloads" />
              <MetricCard title="Conv. Rate" value={`${metrics.conversion_rate}%`} icon={TrendingUp} testId="metric-conversion" />
              <MetricCard title="Active Users" value={metrics.active_users || 0} icon={Activity} testId="metric-active" />
            </div>

            {/* Tier Distribution & Event Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bento-card p-4 sm:p-6">
                <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 sm:mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
                  Tier Distribution
                </h3>
                <div className="space-y-3">
                  <TierBar label="Free" count={metrics.tier_counts.free} total={metrics.total_emails} color="zinc" />
                  <TierBar label="Early Adopter" count={metrics.tier_counts.early_adopter} total={metrics.total_emails} color="blue" />
                  <TierBar label="Standard" count={metrics.tier_counts.standard} total={metrics.total_emails} color="green" />
                </div>
              </div>

              <div className="bento-card p-4 sm:p-6">
                <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 sm:mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
                  Event Breakdown
                </h3>
                <div className="space-y-2">
                  {Object.entries(metrics.event_counts).map(([event, count]) => (
                    <div key={event} className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-zinc-400 truncate mr-2">{event.replace(/_/g, " ")}</span>
                      <span className="text-white font-medium flex-shrink-0">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bento-card p-4 sm:p-6">
              <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 sm:mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
                Recent Activity
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {events.slice(0, 10).map((event, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-white/5 text-zinc-400 whitespace-nowrap flex-shrink-0">
                        {event.event_type.replace(/_/g, " ")}
                      </span>
                      {event.email && (
                        <span className="text-xs sm:text-sm text-zinc-300 truncate">{truncateEmail(event.email, 15)}</span>
                      )}
                    </div>
                    <span className="text-[10px] sm:text-xs text-zinc-500 flex-shrink-0">{formatDateShort(event.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex gap-2 flex-1">
                <Input
                  placeholder="Search email/name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="bg-[#121214] border-white/10 text-white text-sm h-10 flex-1"
                  data-testid="search-input"
                />
                <Button onClick={handleSearch} className="btn-primary h-10 px-3" data-testid="search-btn">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); }}>
                  <SelectTrigger className="w-full sm:w-36 bg-[#121214] border-white/10 text-white text-xs sm:text-sm h-10" data-testid="tier-filter">
                    <SelectValue placeholder="All Tiers" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    <SelectItem value="all" className="text-white text-sm">All Tiers</SelectItem>
                    <SelectItem value="free" className="text-white text-sm">Free</SelectItem>
                    <SelectItem value="early_adopter" className="text-white text-sm">Early Adopter</SelectItem>
                    <SelectItem value="standard" className="text-white text-sm">Standard</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleExportCSV} variant="outline" className="btn-secondary h-10 px-3" data-testid="export-csv-btn">
                  <FileDown className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">CSV</span>
                </Button>
              </div>
            </div>

            {/* Users - Mobile Cards */}
            <div className="block sm:hidden space-y-3">
              {leads.map((lead) => (
                <div key={lead.id} className="mobile-card">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Email</span>
                    <span className="mobile-card-value text-blue-400">{truncateEmail(lead.email)}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Name</span>
                    <span className="mobile-card-value">{lead.name || "-"}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Generations</span>
                    <span className="mobile-card-value">{lead.generation_count || 0}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Joined</span>
                    <span className="mobile-card-value">{formatDateShort(lead.created_at)}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className={`px-2 py-1 text-xs rounded border ${getTierBadgeClass(lead.tier)}`}>
                      {lead.tier?.replace(/_/g, " ") || "free"}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetUsage(lead.email)}
                        className="h-8 px-2 text-zinc-400 hover:text-white"
                        title="Reset usage"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Select 
                        value={lead.tier || "free"} 
                        onValueChange={(v) => handleTierChange(lead.email, v)}
                      >
                        <SelectTrigger className="w-24 h-8 bg-transparent border-white/10 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121214] border-white/10">
                          <SelectItem value="free" className="text-white text-xs">Free</SelectItem>
                          <SelectItem value="early_adopter" className="text-white text-xs">Early Adopter</SelectItem>
                          <SelectItem value="standard" className="text-white text-xs">Standard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
              {leads.length === 0 && (
                <div className="text-center py-12 text-zinc-500 text-sm">No users found</div>
              )}
            </div>

            {/* Desktop Table */}
            <div className="hidden sm:block bento-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Email</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Name</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Tier</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Generations</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Joined</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3 text-sm text-white font-medium truncate max-w-[200px]">{lead.email}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{lead.name || "-"}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded border ${getTierBadgeClass(lead.tier)}`}>
                            {lead.tier?.replace(/_/g, " ") || "free"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{lead.generation_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{formatDate(lead.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetUsage(lead.email)}
                              className="h-8 px-2 text-zinc-400 hover:text-white"
                              title="Reset usage count"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            <Select 
                              value={lead.tier || "free"} 
                              onValueChange={(v) => handleTierChange(lead.email, v)}
                            >
                              <SelectTrigger className="w-32 h-8 bg-transparent border-white/10 text-white text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121214] border-white/10">
                                <SelectItem value="free" className="text-white text-xs">Free</SelectItem>
                                <SelectItem value="early_adopter" className="text-white text-xs">Early Adopter</SelectItem>
                                <SelectItem value="standard" className="text-white text-xs">Standard</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {leads.length === 0 && (
                <div className="text-center py-12 text-zinc-500">No users found</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Events Tab */}
        {activeTab === "events" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Mobile Cards */}
            <div className="block sm:hidden space-y-3">
              {events.map((event, index) => (
                <div key={index} className="mobile-card">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Event</span>
                    <span className="px-2 py-0.5 text-xs rounded bg-white/5 text-zinc-300">
                      {event.event_type.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Email</span>
                    <span className="mobile-card-value">{event.email ? truncateEmail(event.email) : "-"}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Time</span>
                    <span className="mobile-card-value">{formatDateShort(event.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden sm:block bento-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Event Type</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Email</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Metadata</th>
                      <th className="text-left px-4 py-3 text-xs text-zinc-400 font-medium">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, index) => (
                      <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs rounded bg-white/5 text-zinc-300">
                            {event.event_type.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-white">{event.email || "-"}</td>
                        <td className="px-4 py-3 text-xs text-zinc-400 font-mono max-w-[200px] truncate">
                          {event.metadata ? JSON.stringify(event.metadata) : "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{formatDate(event.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl space-y-6"
          >
            {/* Links Settings */}
            <div className="bento-card p-4 sm:p-6">
              <h3 className="text-sm sm:text-lg font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                <Link className="w-4 h-4 sm:w-5 sm:h-5" />
                Download & Payment Links
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs sm:text-sm text-zinc-300">Download Link (ParamSnap.zip)</Label>
                  <Input
                    value={settingsForm.download_link}
                    onChange={(e) => setSettingsForm({ ...settingsForm, download_link: e.target.value })}
                    placeholder="https://github.com/kareiontech/paramsnap-fusion-addon/releases/download/v1.1/ParamSnap_v1_1.zip"
                    className="mt-1 bg-[#0A0A0B] border-white/10 text-white text-sm"
                  />
                </div>
                
                <div>
                  <Label className="text-xs sm:text-sm text-zinc-300">Early Adopter Stripe Link ($50/yr)</Label>
                  <Input
                    value={settingsForm.stripe_early_adopter_link}
                    onChange={(e) => setSettingsForm({ ...settingsForm, stripe_early_adopter_link: e.target.value })}
                    placeholder="https://buy.stripe.com/..."
                    className="mt-1 bg-[#0A0A0B] border-white/10 text-white text-sm"
                  />
                </div>
                
                <div>
                  <Label className="text-xs sm:text-sm text-zinc-300">Standard Stripe Link ($100/yr)</Label>
                  <Input
                    value={settingsForm.stripe_standard_link}
                    onChange={(e) => setSettingsForm({ ...settingsForm, stripe_standard_link: e.target.value })}
                    placeholder="https://buy.stripe.com/..."
                    className="mt-1 bg-[#0A0A0B] border-white/10 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Tier Settings */}
            <div className="bento-card p-4 sm:p-6">
              <h3 className="text-sm sm:text-lg font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                Tier Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs sm:text-sm text-zinc-300">Early Adopter Cap</Label>
                  <p className="text-[10px] sm:text-xs text-zinc-500 mb-2">
                    Max Early Adopter users. Current: {metrics?.tier_counts?.early_adopter || 0}
                  </p>
                  <Input
                    type="number"
                    value={settingsForm.early_adopter_cap}
                    onChange={(e) => setSettingsForm({ ...settingsForm, early_adopter_cap: parseInt(e.target.value) || 0 })}
                    className="bg-[#0A0A0B] border-white/10 text-white w-32 h-10 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button 
              onClick={handleSaveSettings} 
              disabled={settingsSaving}
              className="btn-primary w-full sm:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              {settingsSaving ? "Saving..." : "Save Settings"}
            </Button>
          </motion.div>
        )}

        {/* Admins Tab */}
        {activeTab === "admins" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm sm:text-lg font-semibold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
                Admin Users
              </h3>
              <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
                <DialogTrigger asChild>
                  <Button className="btn-primary text-sm">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Admin
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121214] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle>Add Admin User</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      Create a new admin account. They will be required to change their password on first login.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label className="text-sm text-zinc-300">Email</Label>
                      <Input
                        type="email"
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="mt-1 bg-[#0A0A0B] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-zinc-300">Name (optional)</Label>
                      <Input
                        value={newAdminName}
                        onChange={(e) => setNewAdminName(e.target.value)}
                        placeholder="Admin Name"
                        className="mt-1 bg-[#0A0A0B] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-zinc-300">Initial Password</Label>
                      <Input
                        type="password"
                        value={newAdminPassword}
                        onChange={(e) => setNewAdminPassword(e.target.value)}
                        placeholder="Minimum 8 characters"
                        className="mt-1 bg-[#0A0A0B] border-white/10 text-white"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddAdmin(false)} className="btn-secondary">
                      Cancel
                    </Button>
                    <Button onClick={handleAddAdmin} disabled={addingAdmin} className="btn-primary">
                      {addingAdmin ? "Creating..." : "Create Admin"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bento-card overflow-hidden">
              <div className="divide-y divide-white/5">
                {adminUsers.map((adminUser) => (
                  <div key={adminUser.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white font-medium">{adminUser.email}</p>
                      <p className="text-xs text-zinc-400">
                        {adminUser.name || "No name"} · {adminUser.role}
                        {adminUser.must_change_password && (
                          <span className="ml-2 text-yellow-400">(password change required)</span>
                        )}
                      </p>
                    </div>
                    {adminUser.id !== admin?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAdmin(adminUser.id, adminUser.email)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Hide scrollbar utility */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, testId }) => (
  <div className="bento-card p-4 sm:p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-1 sm:mb-2">
      <span className="text-[10px] sm:text-sm text-zinc-400">{title}</span>
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
    </div>
    <p className="text-xl sm:text-3xl font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
      {value}
    </p>
  </div>
);

// Tier Distribution Bar
const TierBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const colorClasses = {
    zinc: "bg-zinc-500",
    blue: "bg-blue-500",
    green: "bg-green-500"
  };
  
  return (
    <div>
      <div className="flex justify-between text-xs sm:text-sm mb-1">
        <span className="text-zinc-400">{label}</span>
        <span className="text-white">{count}</span>
      </div>
      <div className="h-1.5 sm:h-2 bg-white/5 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
